import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { MAX_CONTACT_LENGTH, MAX_NAME_LENGTH, MIN_PASSWORD_LENGTH } from '@emr/shared';

import {
  startPatientRegistration,
  verifyPatientRegistration,
} from '../../api/patientPortal/auth.ts';
import { patientPortalErrorMessage } from '../../api/patientPortal/errors.ts';
import { LogoLockup } from '../../components/Logo.tsx';
import { Button, ErrorText, Field } from '../../components/primitives.tsx';
import { ThemeMenu, ThemeRejectionNotice } from '../../theme/ThemeMenu.tsx';
import { PortalHelpLink } from '../help/PortalHelpLink.tsx';
import { defaultPortalRedirect } from '../session/portalRedirectTarget.ts';
import { PATIENT_SESSION_QUERY_KEY } from '../session/PatientSessionContext.ts';

/**
 * Patient self-registration (`BACKLOG.md` W3, `docs/decisions/patient-portal-scope.md`'s
 * "Identity" section) — two steps, matching the server's own two-call shape exactly:
 *
 *   1. `startPatientRegistration` — demographics plus a chosen password. The response is always the
 *      same shape (`registrationId` only) regardless of what happened server-side, per that
 *      section's own no-account-existence-oracle requirement — this page shows the identical "check
 *      your messages" copy whether the match was confident, ambiguous, or absent, and never implies
 *      otherwise.
 *   2. `verifyPatientRegistration` — the one-time code sent to whatever contact channel is already
 *      on the matched patient record (never to the address just typed into step 1's form, which
 *      this page never even sends to that step). Success starts a session directly, matching
 *      `patientPortalRegisterVerifyRequestSchema`'s own 201 behaviour.
 *
 * A failed or still-pending (ambiguous-match, staff-review-queued) verification looks identical to
 * a wrong code from here — `patientPortalErrorMessage`'s `invalid_or_expired_registration` copy is
 * deliberately non-committal for exactly that reason.
 */
export function PortalRegisterPage() {
  const { t } = useTranslation('portalAuth');
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<'start' | 'verify'>('start');
  const [registrationId, setRegistrationId] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  const [code, setCode] = useState('');

  const start = useMutation({
    mutationFn: startPatientRegistration,
    onSuccess: (response) => {
      setRegistrationId(response.registrationId);
      setStep('verify');
    },
  });

  const verify = useMutation({
    mutationFn: verifyPatientRegistration,
    onSuccess: (patient) => {
      queryClient.setQueryData(PATIENT_SESSION_QUERY_KEY, patient);
      if (orgId !== undefined) void navigate(defaultPortalRedirect(orgId), { replace: true });
    },
  });

  if (orgId === undefined) {
    return <Navigate to="/login" replace />;
  }

  return (
    <main id="main" className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center p-4">
      <div className="mb-6 flex items-center justify-between">
        <LogoLockup size={40} />
        <ThemeMenu />
      </div>
      <ThemeRejectionNotice />

      <div className="rounded-panel border border-line bg-surface p-4 shadow-panel sm:p-6">
        {step === 'start' ? (
          <>
            <div className="mb-1 flex items-center gap-2">
              <h1 className="m-0 text-xl font-semibold text-ink">{t('register.heading')}</h1>
              <PortalHelpLink topicId="creating-your-account" />
            </div>
            <p className="mt-0 mb-5 text-sm text-ink-muted">{t('register.subtitle')}</p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                setPasswordMismatch(false);
                if (password !== confirmPassword) {
                  setPasswordMismatch(true);
                  return;
                }
                start.mutate({
                  orgId,
                  firstName,
                  lastName,
                  dateOfBirth,
                  ...(phone.trim() === '' ? {} : { phone }),
                  ...(email.trim() === '' ? {} : { email }),
                  password,
                });
              }}
              className="flex flex-col gap-4"
            >
              <Field
                label={t('register.firstNameLabel')}
                name="firstName"
                required
                maxLength={MAX_NAME_LENGTH}
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => {
                  setFirstName(event.target.value);
                }}
              />
              <Field
                label={t('register.lastNameLabel')}
                name="lastName"
                required
                maxLength={MAX_NAME_LENGTH}
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => {
                  setLastName(event.target.value);
                }}
              />
              <Field
                label={t('register.dateOfBirthLabel')}
                type="date"
                name="dateOfBirth"
                required
                autoComplete="bday"
                value={dateOfBirth}
                onChange={(event) => {
                  setDateOfBirth(event.target.value);
                }}
              />
              <Field
                label={t('register.phoneLabel')}
                hint={t('register.contactHint')}
                type="tel"
                name="phone"
                maxLength={MAX_CONTACT_LENGTH}
                autoComplete="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                }}
              />
              <Field
                label={t('register.emailLabel')}
                type="email"
                name="email"
                maxLength={MAX_CONTACT_LENGTH}
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
              <Field
                label={t('register.passwordLabel')}
                hint={t('register.passwordHint', { min: MIN_PASSWORD_LENGTH })}
                type="password"
                name="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                autoComplete="new-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
              <Field
                label={t('register.confirmPasswordLabel')}
                type="password"
                name="confirmPassword"
                required
                autoComplete="new-password"
                value={confirmPassword}
                {...(passwordMismatch ? { error: t('register.passwordMismatch') } : {})}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                }}
              />

              {start.isError ? (
                <ErrorText message={patientPortalErrorMessage(start.error)} />
              ) : null}

              <Button type="submit" pending={start.isPending} className="w-full">
                {start.isPending ? t('register.submitting') : t('register.submit')}
              </Button>
            </form>
          </>
        ) : (
          <>
            <div className="mb-1 flex items-center gap-2">
              <h1 className="m-0 text-xl font-semibold text-ink">{t('verify.heading')}</h1>
              <PortalHelpLink topicId="creating-your-account" />
            </div>
            <p className="mt-0 mb-5 text-sm text-ink-muted">{t('verify.subtitle')}</p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (registrationId === null) return;
                verify.mutate({ orgId, registrationId, code });
              }}
              className="flex flex-col gap-4"
            >
              <Field
                label={t('verify.codeLabel')}
                name="code"
                required
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
              />

              {verify.isError ? (
                <ErrorText message={patientPortalErrorMessage(verify.error)} />
              ) : null}

              <Button type="submit" pending={verify.isPending} className="w-full">
                {verify.isPending ? t('verify.submitting') : t('verify.submit')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setStep('start');
                }}
              >
                {t('verify.startOver')}
              </Button>
            </form>
          </>
        )}

        <p className="mt-4 mb-0 text-sm text-ink-muted">
          {t('register.haveAccount')}{' '}
          <Link to={`/portal/${orgId}/login`} className="font-medium text-accent-text">
            {t('register.signInLink')}
          </Link>
        </p>
      </div>
    </main>
  );
}
